import { createAction } from "redux-actions";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { api } from "../api";

export const SET_LOADING = "saga/customCharts/SET_LOADING";
export const SET_STATUS = "saga/customCharts/SET_STATUS";
export const SET_ERROR = "saga/customCharts/SET_ERROR";

export const CREATE_MODULE = "saga/customCharts/CREATE_MODULE";
export const FETCH_MODULES = "saga/customCharts/FETCH_MODULES";
export const FETCH_MODULES_SUCCESS = "saga/customCharts/FETCH_MODULES_SUCCESS";
export const CHANGE_MODULE_POSITION = "saga/customCharts/CHANGE_MODULE_POSITION";
export const EDIT_MODULE = "saga/customCharts/EDIT_MODULE";
export const EDIT_MODULE_SUCCESS = "saga/customCharts/EDIT_MODULE_SUCCESS";
export const DELETE_MODULE = "saga/customCharts/DELETE_MODULE";
export const DELETE_MODULE_SUCCESS = "saga/customCharts/REMOVE_CHART_SUCCESS";

export const FETCH_EDIT_PREVIEW = "saga/customCharts/FETCH_EDIT_PREVIEW";
export const FETCH_EDIT_PREVIEW_SUCCESS = "saga/customCharts/EDIT_PREVIEW_SUCCESS";
export const CHANGE_PREVIEW_CHART_TYPE = "saga/CHANGE_PREVIEW_CHART_TYPE";
export const FETCH_PREVIEW_CHART_SUCCESS = "saga/customCharts/FETCH_PREVIEW_CHART_SUCCESS";
export const RESET_PREVIEW = "saga/customCharts/RESET_PREVIEW";

export const FETCH_CATEGORIES = "saga/customCharts/FETCH_CATEGORIES";
export const FETCH_CATEGORIES_SUCCESS = "saga/customCharts/FETCH_CATEGORIES_SUCCESS";
export const SET_CATEGORY = "saga/customCharts/SET_CATEGORY";
export const CHANGE_CATEGORY = "saga/customCharts/CHANGE_CATEGORY";
export const SET_CATEGORIES_LOADING = "saga/customCharts/SET_CATEGORIES_LOADING";

const actionCreators = {
  getModules: createAction(FETCH_MODULES),
  createModule: createAction(CREATE_MODULE),
  changeModulePosition: createAction(CHANGE_MODULE_POSITION),
  editModule: createAction(EDIT_MODULE),
  deleteModule: createAction(DELETE_MODULE),
  getCategories: createAction(FETCH_CATEGORIES),
  changeCategory: createAction(CHANGE_CATEGORY),
  changeChartType: createAction(CHANGE_PREVIEW_CHART_TYPE),
  getEditPreview: createAction(FETCH_EDIT_PREVIEW),
  resetPreview: createAction(RESET_PREVIEW)
};

export const {
  getModules,
  createModule,
  changeModulePosition,
  editModule,
  deleteModule,
  getCategories,
  changeCategory,
  changeChartType,
  getEditPreview,
  resetPreview
} = actionCreators;

export default function* watchCustomCharts() {
  yield takeLatestSafe(FETCH_CATEGORIES, fetchCategoryList);
  yield takeLatestSafe(CHANGE_CATEGORY, updateCategory);
  yield takeLatestSafe(CHANGE_PREVIEW_CHART_TYPE, fetchPreviewChart);
  yield takeLatestSafe(CREATE_MODULE, fetchCreateModule);
  yield takeLatestSafe(DELETE_MODULE, fetchDeleteChart);
  yield takeLatestSafe(CHANGE_MODULE_POSITION, fetchChangePosition);
  yield takeLatestSafe(FETCH_MODULES, fetchModules);
  yield takeLatestSafe(FETCH_EDIT_PREVIEW, fetchEditPreview);
  yield takeLatestSafe(EDIT_MODULE, fetchEditModule);
}

function takeLatestSafe(action, effect) {
  return takeLatest(action, tryCatch(effect));
}

function tryCatch(effect) {
  return function* (args) {
    try {
      yield call(effect, args);
    } catch (err) {
      console.error(err);
      if (err.response.status === 422) {
        yield put({ type: SET_ERROR, payload: err.response.data });
      }
    }
  };
}

function* updateCategory({ payload }) {
  yield put({ type: SET_CATEGORY, payload });
  yield fetchPreviewChart();
}

function* fetchCategoryList() {
  const categories = yield select((state) => state.customCharts.categories);

  if (categories.length) return;
  yield put({ type: SET_CATEGORIES_LOADING, payload: true });

  const { data } = yield call(api.getCategoryList);

  yield put({
    type: FETCH_CATEGORIES_SUCCESS,
    payload: data
  });
  yield put({ type: SET_CATEGORIES_LOADING, payload: false });
}

function* fetchPreviewChart() {
  const {
    category_id,
    category_target_id,
    compare_category_id,
    compare_category_target_id,
    graph_type,
    short_url
  } = yield getCommonApiParams();
  if (!category_id) return;

  const params = {
    short_url,
    custom_graph: {
      category_id,
      compare_category_id,
      category_type: "Metric",
      compare_category_type: "Metric",
      category_target_id,
      compare_category_target_id,
      graph_type
    }
  };
  const { data } = yield call(api.getPreviewChart, params);

  yield put({ type: FETCH_PREVIEW_CHART_SUCCESS, payload: data });
}

function* fetchCreateModule({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });
  const {
    category_id,
    category_target_id,
    compare_category_id,
    compare_category_target_id,
    graph_type
  } = yield getCommonApiParams();

  const params = {
    custom_graph: {
      category_id,
      compare_category_id,
      category_type: "Metric",
      compare_category_type: "Metric",
      category_target_id,
      compare_category_target_id,
      graph_type,
      name: payload.name
    }
  };
  yield call(api.createModule, params);
  yield fetchModules();

  yield put({ type: SET_STATUS, payload: "success" });
  yield put({ type: RESET_PREVIEW });
}

function* fetchEditModule({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  const {
    category_id,
    category_target_id,
    compare_category_id,
    compare_category_target_id,
    graph_type
  } = yield getCommonApiParams();

  const params = {
    custom_graph: {
      category_id: category_id,
      compare_category_id,
      category_type: "Metric",
      compare_category_type: "Metric",
      category_target_id,
      compare_category_target_id,
      graph_type,
      name: payload.name
    }
  };

  yield put({ type: SET_LOADING, payload: true });

  const { data } = yield call(api.editModule, payload.id, params);

  yield put({ type: EDIT_MODULE_SUCCESS, payload: data });
  yield put({ type: SET_STATUS, payload: "success" });
  yield put({ type: SET_LOADING, payload: false });
  yield put({ type: RESET_PREVIEW });
}

function* fetchChangePosition({ payload }) {
  const { moduleId, direction } = payload;

  yield call(api.changeModulePosition, moduleId, direction);
  yield fetchModules();
}

function* fetchEditPreview({ payload }) {
  yield put({ type: SET_STATUS, payload: "pending" });

  yield fetchCategoryList();

  const { data } = yield call(api.getPreview, payload);
  yield put({ type: FETCH_EDIT_PREVIEW_SUCCESS, payload: data });
  yield put({ type: SET_STATUS, payload: "idle" });
}

function* fetchDeleteChart({ payload }) {
  yield call(api.deleteModule, payload.id);
  yield put({ type: DELETE_MODULE_SUCCESS, payload: payload.id });
}

function* fetchModules() {
  yield put({ type: SET_LOADING, payload: true });

  const { data } = yield call(api.getModules);

  yield put({ type: FETCH_MODULES_SUCCESS, payload: data });
  yield put({ type: SET_LOADING, payload: false });
}

export function* getCommonApiParams() {
  return yield select((state) => {
    const {
      selectedCategories: { category, category_target, compare_category, compare_category_target },
      graph_type
    } = state.customCharts.previewModalData;
    return {
      category_id: category?.id,
      category_target_id: category_target?.id,
      compare_category_id: compare_category?.id || null,
      compare_category_target_id: compare_category_target?.id,
      graph_type
    };
  });
}
